import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Badge, Button, PageHeader, Panel, PanelBody, ProgressBar, SectionLabel } from '../ui/index.js';
import { fetchJson, ApiError } from '../lib/fetch-json.js';
import {
  fetchWooWebhooks,
  installWooWebhooks,
  runWooMirrorNow,
  me,
  fetchWooOrdersStatus,
  syncWooOrders,
} from '../api.js';

/**
 * S10 — Woo Engine: ALL push-engine info lives on this page (client request).
 * The operator enters the WooCommerce URL + API keys here; they are stored in
 * the app database (app_setting) — Coolify env remains a fallback, never a
 * requirement. The EAN meta key MUST match the WPFactory plugin setting
 * (Advanced → Meta key; this store uses alg_ean) or REF writes miss silently.
 * Secrets are write-only: the GET returns masked previews, an empty field on
 * save keeps the stored value.
 */

interface WooEngineConfigView {
  readonly eanMetaKey: string;
  readonly wooEnv: 'staging' | 'production';
  readonly baseUrl: string | null;
  readonly credentialsSet: boolean;
  readonly consumerKeyPreview: string | null;
  readonly consumerSecretPreview: string | null;
  readonly wpUser?: string | null;
  readonly wpAppPasswordSet?: boolean;
  readonly wpAppPasswordPreview?: string | null;
  readonly source: 'page' | 'env';
  readonly webhookConfigured: boolean;
  readonly webhookSecretPreview: string | null;
  readonly webhookDeliveryUrl: string;
}

interface WooEngineDraft {
  readonly eanMetaKey: string;
  readonly wooEnv: 'staging' | 'production';
  readonly baseUrl: string;
  readonly consumerKey: string;
  readonly consumerSecret: string;
  readonly wpUser: string;
  readonly wpAppPassword: string;
}

interface TestConnectionResult {
  readonly ok: boolean;
  readonly wooEnv?: string;
  readonly baseUrl?: string;
  readonly sample?: string;
  readonly error?: string;
}

interface FlaggedItem {
  readonly id: number;
  readonly parentId: number | null;
  readonly kind: 'product' | 'variation';
  readonly name: string;
  readonly sku: string;
  readonly virtual: boolean;
  readonly downloadable: boolean;
}

interface FlagScanPage {
  readonly ok: boolean;
  readonly page: number;
  readonly totalPages: number;
  readonly scanned: number;
  readonly flagged: readonly FlaggedItem[];
  readonly error?: string;
}

const inputCls =
  'h-8.5 w-full rounded-lg border border-border bg-card px-2.5 font-mono text-xs text-ink placeholder:text-ink-ghost focus:border-herb-600 focus:ring-2 focus:ring-herb-500/20 outline-none';

function WooEnginePageInner(): React.ReactElement {
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: me, retry: false });
  const canEdit = meQuery.data?.role === 'admin' || meQuery.data?.role === 'reviewer';
  const isAdmin = meQuery.data?.role === 'admin';

  const configQuery = useQuery({
    queryKey: ['woo-engine-config'],
    queryFn: () => fetchJson<WooEngineConfigView>('/api/v1/woo-engine/config'),
  });
  const cfg = configQuery.data;

  const [draft, setDraft] = useState<WooEngineDraft | null>(null);
  const form: WooEngineDraft =
    draft ?? {
      eanMetaKey: cfg?.eanMetaKey ?? 'alg_ean',
      wooEnv: cfg?.wooEnv ?? 'staging',
      baseUrl: cfg?.baseUrl ?? '',
      consumerKey: '',
      consumerSecret: '',
      wpUser: cfg?.wpUser ?? '',
      wpAppPassword: '',
    };
  const dirty = draft !== null;
  const patch = (p: Partial<WooEngineDraft>): void => setDraft({ ...form, ...p });

  // paste noise (copied from chats/emails): zero-width chars survive trim and
  // are invisible — strip them; add the protocol if the operator omitted it
  const cleanPaste = (v: string): string => v.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();

  const saveConfig = useMutation({
    mutationFn: () => {
      let baseUrl = cleanPaste(form.baseUrl);
      if (baseUrl !== '' && !/^https?:\/\//i.test(baseUrl)) {
        baseUrl = `https://${baseUrl}`;
      }
      return fetchJson('/api/v1/woo-engine/config', {
        method: 'PUT',
        body: JSON.stringify({
          eanMetaKey: cleanPaste(form.eanMetaKey),
          wooEnv: form.wooEnv,
          baseUrl,
          // empty secret fields = keep the stored ones (write-only)
          consumerKey: cleanPaste(form.consumerKey),
          consumerSecret: cleanPaste(form.consumerSecret),
          wpUser: cleanPaste(form.wpUser),
          wpAppPassword: cleanPaste(form.wpAppPassword),
        }),
      });
    },
    onSuccess: () => {
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: ['woo-engine-config'] });
    },
  });

  const testConnection = useQuery({
    queryKey: ['woo-engine-test'],
    queryFn: () => fetchJson<TestConnectionResult>('/api/v1/woo-engine/test-connection'),
    enabled: false,
    retry: false,
  });

  const webhooksQuery = useQuery({
    queryKey: ['woo-webhooks'],
    queryFn: fetchWooWebhooks,
    enabled: canEdit,
    refetchInterval: 60_000,
  });
  const installHooks = useMutation({
    mutationFn: installWooWebhooks,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['woo-webhooks'] });
      void queryClient.invalidateQueries({ queryKey: ['woo-engine-config'] });
    },
  });

  const runMirror = useMutation({
    mutationFn: runWooMirrorNow,
    onSuccess: () => {
      setMirrorQueued(true);
      void queryClient.invalidateQueries({ queryKey: ['woo-orders-status'] });
    },
  });
  const [mirrorQueued, setMirrorQueued] = useState(false);

  const ordersStatusQuery = useQuery({
    queryKey: ['woo-orders-status'],
    queryFn: fetchWooOrdersStatus,
    enabled: canEdit,
    refetchInterval: (query) => {
      const data = query.state.data;
      const isOrdersRunning = data?.ordersSyncProgress?.running === true;
      const isMirrorRunning = data?.mirrorProgress?.running === true;
      return isOrdersRunning || isMirrorRunning ? 2_000 : 30_000;
    },
  });

  const [ordersSyncQueued, setOrdersSyncQueued] = useState<{ forceFull: boolean } | null>(null);

  const syncOrders = useMutation({
    mutationFn: (forceFull: boolean) => syncWooOrders(forceFull),
    onSuccess: (_data, forceFull) => {
      setOrdersSyncQueued({ forceFull });
      void queryClient.invalidateQueries({ queryKey: ['woo-orders-status'] });
    },
  });

  const testPush = useMutation({
    mutationFn: () => fetchJson('/api/v1/wc-sync/plan', { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['wc-sync'] }),
  });

  // virtual/downloadable scan: page-by-page direct reads of the Woo catalog;
  // the operator fixes the flagged products manually in the Woo admin
  const [flagScan, setFlagScan] = useState<{
    running: boolean;
    page: number;
    totalPages: number;
    found: readonly FlaggedItem[];
    error: string | null;
    done: boolean;
  }>({ running: false, page: 0, totalPages: 0, found: [], error: null, done: false });

  const runFlagScan = async (): Promise<void> => {
    setFlagScan({ running: true, page: 0, totalPages: 0, found: [], error: null, done: false });
    const found: FlaggedItem[] = [];
    let page = 1;
    let totalPages = 1;
    try {
      while (page <= totalPages) {
        const r = await fetchJson<FlagScanPage>(`/api/v1/woo-engine/flag-scan?page=${page}`, {
          signal: AbortSignal.timeout(120_000),
        });
        if (!r.ok) throw new Error(r.error ?? `échec page ${page}`);
        totalPages = r.totalPages;
        found.push(...r.flagged);
        setFlagScan({
          running: true,
          page,
          totalPages,
          found: [...found],
          error: null,
          done: false,
        });
        page += 1;
      }
      setFlagScan({ running: false, page: totalPages, totalPages, found, error: null, done: true });
    } catch (error) {
      setFlagScan({
        running: false,
        page,
        totalPages,
        found,
        error: error instanceof Error ? error.message : String(error),
        done: false,
      });
    }
  };

  const editUrl = (f: FlaggedItem): string => {
    const base = (cfg?.baseUrl ?? '').replace(/\/+$/, '');
    const postId = f.parentId ?? f.id;
    return `${base}/wp-admin/post.php?post=${postId}&action=edit`;
  };

  const copyFlagList = (): void => {
    const lines = flagScan.found.map(
      (f) =>
        `${f.kind === 'variation' ? 'variante' : 'produit'} #${f.id}${f.sku !== '' ? ` · ${f.sku}` : ''} · ${f.name} · ${f.virtual ? 'virtual' : ''}${f.downloadable ? ' downloadable' : ''} · ${editUrl(f)}`,
    );
    void navigator.clipboard.writeText(lines.join('\n')).catch(() => undefined);
  };

  const metaKeyMatchesPlugin = form.eanMetaKey.trim() === 'alg_ean';

  return (
    <div>
      <PageHeader
        index="S10"
        title="Moteur WooCommerce"
        purpose="Toute la configuration du moteur de poussée vit sur cette page : URL et clés de l'API WooCommerce, environnement cible, clé méta EAN (plugin WPFactory). Saisie ici = stockée dans la base de l'application ; l'env Coolify n'est qu'un secours de premier démarrage."
        meta={[
          { label: 'Clé méta EAN', value: cfg?.eanMetaKey ?? '—' },
          {
            label: 'Environnement',
            value: cfg?.wooEnv ?? '—',
          },
          {
            label: 'Credentials',
            value:
              cfg === undefined
                ? '…'
                : cfg.credentialsSet
                  ? `configuré (${cfg.source === 'page' ? 'cette page' : 'env Coolify'})`
                  : 'non configuré',
          },
        ]}
      />

      <div className="grid gap-4 xl:grid-cols-2">
        {/* EAN meta key — the WPFactory plugin field */}
        <Panel>
          <PanelBody className="space-y-3">
            <SectionLabel>Champ REF (plugin EAN Barcode Generator)</SectionLabel>
            <p className="text-xs leading-relaxed text-ink-soft">
              Le plugin WPFactory stocke le champ <strong>REF</strong> dans une méta
              dont la clé est configurée dans ses options avancées. Votre boutique
              utilise <code className="font-mono">alg_ean</code>. Le moteur écrit
              les <strong>deux</strong> champs en une requête :{' '}
              <code className="font-mono">global_unique_id</code> (champ cœur
              « GTIN, UPC, EAN, ou ISBN ») + cette méta. Une clé incorrecte fait
              perdre le champ REF en silence.
            </p>
            {!metaKeyMatchesPlugin && (
              <Alert tone="alarm" title="Clé méta probablement incorrecte">
                La clé saisie est « {form.eanMetaKey} » mais votre plugin
                (capture d’écran 2026-10-07) utilise « alg_ean ». Corrigez-la
                avant tout envoi.
              </Alert>
            )}
            <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
              Clé méta EAN
              <input
                value={form.eanMetaKey}
                disabled={!canEdit}
                onChange={(event) => patch({ eanMetaKey: event.target.value })}
                placeholder="alg_ean"
                className={`${inputCls} w-48`}
              />
            </label>
          </PanelBody>
        </Panel>

        {/* API credentials — editable HERE, stored in the app database */}
        <Panel>
          <PanelBody className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <SectionLabel>Credentials API WooCommerce</SectionLabel>
              {cfg !== undefined && (
                <Badge tone={cfg.source === 'page' ? 'good' : cfg.credentialsSet ? 'signal' : 'alarm'}>
                  {cfg.source === 'page'
                    ? 'cette page'
                    : cfg.credentialsSet
                      ? 'secours Coolify env'
                      : 'non configuré'}
                </Badge>
              )}
            </div>
            <p className="text-xs leading-relaxed text-ink-soft">
              Saisissez l’URL du site et les clés consommateur Woo (WooCommerce →
              Réglages → Avancé → API REST). Elles sont enregistrées dans la base
              de l’application et utilisées par le worker dès le prochain envoi.
              Les secrets ne sont jamais réaffichés — laisser vide = conserver
              l’ancienne valeur.
            </p>
            <div className="grid gap-2.5">
              <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
                Environnement cible des poussées
                <select
                  value={form.wooEnv}
                  disabled={!canEdit}
                  onChange={(event) => patch({ wooEnv: event.target.value === 'production' ? 'production' : 'staging' })}
                  className="rounded-lg border border-border bg-card px-2.5 py-2 text-xs font-semibold text-ink shadow-xs"
                >
                  <option value="staging">staging (vérification d’abord)</option>
                  <option value="production">production</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
                URL du site WooCommerce
                <input
                  value={form.baseUrl}
                  disabled={!canEdit}
                  onChange={(event) => patch({ baseUrl: event.target.value })}
                  placeholder="https://maparatunisie.tn"
                  className={inputCls}
                />
              </label>
              <div className="grid gap-2.5 sm:grid-cols-2">
                <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
                  Clé consommateur (ck)
                  <input
                    value={form.consumerKey}
                    disabled={!canEdit}
                    onChange={(event) => patch({ consumerKey: event.target.value })}
                    placeholder={cfg?.consumerKeyPreview ?? 'ck_…'}
                    autoComplete="off"
                    className={inputCls}
                  />
                </label>
                <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
                  Secret consommateur (cs)
                  <input
                    type="password"
                    value={form.consumerSecret}
                    disabled={!canEdit}
                    onChange={(event) => patch({ consumerSecret: event.target.value })}
                    placeholder={cfg?.consumerSecretPreview ?? 'cs_…'}
                    autoComplete="new-password"
                    className={inputCls}
                  />
                </label>
              </div>

              <div className="mt-2 border-t border-edge-subtle pt-3">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-[11px] font-semibold text-ink-base">
                    Application Password WordPress (optionnel, pour l'édition directe des médias)
                  </span>
                  {cfg?.wpAppPasswordSet && (
                    <Badge tone="good">configuré</Badge>
                  )}
                </div>
                <p className="text-[11px] text-ink-soft mb-2">
                  Permet à Image Studio de mettre à jour directement la légende, la description et le titre de chaque média via l'API REST WordPress (<code className="font-mono">/wp-json/wp/v2/media</code>).
                </p>
                <div className="grid gap-2.5 sm:grid-cols-2">
                  <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
                    Utilisateur WordPress
                    <input
                      value={form.wpUser}
                      disabled={!canEdit}
                      onChange={(event) => patch({ wpUser: event.target.value })}
                      placeholder="admin"
                      className={inputCls}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
                    Mot de passe d'application
                    <input
                      type="password"
                      value={form.wpAppPassword}
                      disabled={!canEdit}
                      onChange={(event) => patch({ wpAppPassword: event.target.value })}
                      placeholder={cfg?.wpAppPasswordPreview ?? 'xxxx xxxx xxxx xxxx'}
                      autoComplete="new-password"
                      className={inputCls}
                    />
                  </label>
                </div>
              </div>
            </div>
            {form.wooEnv === 'production' && (
              <Alert tone="alarm" title="Cible production">
                Les envois (corrections, masquage) partiront sur le site de
                production. La règle maison reste : valider un lot complet sur
                staging avant de basculer ici.
              </Alert>
            )}
            {cfg !== undefined && cfg.credentialsSet && cfg.source === 'env' && (
              <Alert tone="warn" title="Actuellement fourni par l’env Coolify">
                Les clés actives viennent des variables WC_STAGING_* du déploiement.
                Enregistrez-les ici pour les rendre indépendantes de Coolify.
              </Alert>
            )}
            {canEdit && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="primary"
                  size="xs"
                  disabled={
                    saveConfig.isPending ||
                    form.eanMetaKey.trim() === '' ||
                    (form.consumerKey.trim() !== '' && form.consumerKey.trim().length < 10) ||
                    (form.consumerSecret.trim() !== '' && form.consumerSecret.trim().length < 10)
                  }
                  onClick={() => saveConfig.mutate()}
                >
                  {saveConfig.isPending ? 'Enregistrement…' : 'Enregistrer'}
                </Button>
                {dirty && (
                  <Button type="button" variant="ghost" size="xs" onClick={() => setDraft(null)}>
                    Annuler
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  disabled={testConnection.isFetching}
                  onClick={() => void testConnection.refetch()}
                  title="Appelle l’API Woo avec la configuration enregistrée (1 produit de test)"
                >
                  {testConnection.isFetching ? 'Test…' : 'Tester la connexion'}
                </Button>
              </div>
            )}
            {saveConfig.isError && (
              <Alert tone="alarm" title="Échec de l’enregistrement">
                {saveConfig.error instanceof ApiError
                  ? `${saveConfig.error.data.error ?? 'erreur'}${saveConfig.error.data.detail ? ` — ${saveConfig.error.data.detail}` : ''}`
                  : String(saveConfig.error.message)}
              </Alert>
            )}
            {saveConfig.isSuccess && !dirty && (
              <Alert tone="good" title="Enregistré">
                Configuration active — le worker l’utilise au prochain envoi.
              </Alert>
            )}
            {testConnection.data !== undefined && (
              testConnection.data.ok ? (
                <Alert tone="good" title={`Connexion OK (${testConnection.data.wooEnv ?? '?'})`}>
                  API joignable — produit de test : « {testConnection.data.sample || '…'} »
                </Alert>
              ) : (
                <Alert tone="alarm" title="Connexion impossible">
                  {testConnection.data.error === 'no_credentials'
                    ? 'Aucune clé configurée (ni sur cette page, ni en env Coolify).'
                    : (testConnection.data.error ?? 'Erreur inconnue')}
                </Alert>
              )
            )}
          </PanelBody>
        </Panel>
      </div>

      {/* Site webhooks — near-real-time product refresh */}
      <Panel className="mt-4">
        <PanelBody className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SectionLabel>Miroir du catalogue</SectionLabel>
            {mirrorQueued && <Badge tone="good">miroir en file — résultat dans les logs worker</Badge>}
          </div>
          <p className="text-xs leading-relaxed text-ink-soft">
            Relit TOUT le catalogue du site : produits, variantes, catégories,
            flags virtuel/téléchargeable — et met les produits disparus hors
            ligne. Le run nocturne (02:00) reste le filet.
          </p>
          {canEdit && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={runMirror.isPending}
              onClick={() => runMirror.mutate()}
            >
              {runMirror.isPending ? 'Mise en file…' : 'Lancer le miroir maintenant'}
            </Button>
          )}
          {/* Active Mirror Progress Bar & Alerts */}
          {ordersStatusQuery.data?.mirrorProgress?.running ? (
            <div className="space-y-2 p-3.5 rounded-xl border border-sky-200 bg-sky-50/50">
              <ProgressBar
                value={null}
                label={`Miroir catalogue en cours : page ${ordersStatusQuery.data.mirrorProgress.page} · ${ordersStatusQuery.data.mirrorProgress.created} créés, ${ordersStatusQuery.data.mirrorProgress.updated} mis à jour, ${ordersStatusQuery.data.mirrorProgress.missingSku} sans SKU…`}
              />
            </div>
          ) : ordersStatusQuery.data?.mirrorProgress?.error ? (
            <Alert tone="alarm" title="Échec de l'exécution du miroir catalogue">
              {ordersStatusQuery.data.mirrorProgress.error}
            </Alert>
          ) : ordersStatusQuery.data?.mirrorProgress && !ordersStatusQuery.data.mirrorProgress.running && ordersStatusQuery.data.mirrorProgress.doneAt && mirrorQueued ? (
            <Alert tone="good" title="Miroir catalogue terminé">
              Miroir achevé avec succès ({ordersStatusQuery.data.mirrorProgress.page} pages lues · {ordersStatusQuery.data.mirrorProgress.created} créés, {ordersStatusQuery.data.mirrorProgress.updated} mis à jour, {ordersStatusQuery.data.mirrorProgress.missingSku} sans SKU).
            </Alert>
          ) : runMirror.isSuccess && mirrorQueued ? (
            <div className="space-y-2 p-3 rounded-xl border border-sky-200 bg-sky-50/40">
              <ProgressBar value={null} label="Miroir envoyé au worker — démarrage du parcours catalogue…" />
            </div>
          ) : null}

          {runMirror.isError && (
            <Alert tone="alarm" title="Échec de la mise en file du miroir">
              {String(runMirror.error.message)}
            </Alert>
          )}
        </PanelBody>
      </Panel>

      {/* WooCommerce orders mirror & backlog */}
      <Panel className="mt-4">
        <PanelBody className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SectionLabel>Miroir des commandes WooCommerce</SectionLabel>
            {ordersStatusQuery.data !== undefined && (
              <Badge tone="good">
                {ordersStatusQuery.data.totalStored.toLocaleString('fr-FR')} commandes en base
              </Badge>
            )}
          </div>
          <p className="text-xs leading-relaxed text-ink-soft">
            Synchronise les commandes WooCommerce avec la console de confirmation COD (Shop Manager)
            et alimente les statistiques de ventes (S21). Intègre les 20 statuts WooCommerce (core &amp; custom).
            La synchronisation planifiée s&apos;exécute automatiquement toutes les 15 minutes en mode incrémental.
          </p>

          {ordersStatusQuery.data !== undefined && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-cream/40 px-3 py-2 text-xs">
              <span>
                Dernière synchro :{' '}
                <strong className="font-mono text-ink">
                  {ordersStatusQuery.data.lastSyncedAt !== null
                    ? new Date(ordersStatusQuery.data.lastSyncedAt).toLocaleString('fr-FR')
                    : 'jamais'}
                </strong>
              </span>
              <span className="text-border">·</span>
              <span>
                Backfill complet :{' '}
                {ordersStatusQuery.data.fullSyncDoneAt !== null ? (
                  <Badge tone="good">
                    effectué ({new Date(ordersStatusQuery.data.fullSyncDoneAt).toLocaleDateString('fr-FR')})
                  </Badge>
                ) : (
                  <Badge tone="signal">en attente / non finalisé</Badge>
                )}
              </span>
            </div>
          )}

          {canEdit && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={syncOrders.isPending || ordersStatusQuery.data?.ordersSyncProgress?.running}
                onClick={() => syncOrders.mutate(false)}
                title="Télécharge les nouvelles commandes et modifications récentes (~2-5 s)"
              >
                {syncOrders.isPending && !ordersSyncQueued?.forceFull ? 'Mise en file…' : 'Synchroniser les commandes récentes'}
              </Button>

              {isAdmin && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={syncOrders.isPending || ordersStatusQuery.data?.ordersSyncProgress?.running}
                  onClick={() => {
                    const ok = window.confirm(
                      'Forcer un import complet du catalogue de commandes ?\n\n' +
                        'Cette action va re-parcourir toutes les pages WooCommerce depuis 2023 (~72 500+ commandes) sans filtre de date.\n' +
                        "L'opération s'exécute en arrière-plan (~8 à 10 min) sans bloquer le serveur.",
                    );
                    if (ok) syncOrders.mutate(true);
                  }}
                  title="Réservé Administrateur : re-télécharge tout l'historique WooCommerce depuis le début"
                >
                  {syncOrders.isPending && ordersSyncQueued?.forceFull ? 'Mise en file…' : 'Forcer import complet (Admin)'}
                </Button>
              )}
            </div>
          )}

          {/* Active Orders Sync Progress Bar & Alerts */}
          {ordersStatusQuery.data?.ordersSyncProgress?.running ? (
            <div className="space-y-2 p-3.5 rounded-xl border border-herb-200 bg-herb-50/50">
              {ordersStatusQuery.data.ordersSyncProgress.forceFull ? (
                <ProgressBar
                  value={
                    ordersStatusQuery.data.ordersSyncProgress.totalEstimate
                      ? Math.min(
                          99,
                          Math.round(
                            (ordersStatusQuery.data.ordersSyncProgress.fetched /
                              ordersStatusQuery.data.ordersSyncProgress.totalEstimate) *
                              100,
                          ),
                        )
                      : null
                  }
                  label={`Import complet en cours : page ${ordersStatusQuery.data.ordersSyncProgress.page} · ${ordersStatusQuery.data.ordersSyncProgress.fetched.toLocaleString('fr-FR')} commandes (${ordersStatusQuery.data.ordersSyncProgress.upserted.toLocaleString('fr-FR')} enregistrées)`}
                />
              ) : (
                <ProgressBar
                  value={null}
                  label={`Synchronisation des commandes : page ${ordersStatusQuery.data.ordersSyncProgress.page} · ${ordersStatusQuery.data.ordersSyncProgress.fetched} commandes récupérées…`}
                />
              )}
            </div>
          ) : ordersStatusQuery.data?.ordersSyncProgress?.error ? (
            <Alert tone="alarm" title="Échec de la synchronisation des commandes">
              {ordersStatusQuery.data.ordersSyncProgress.error}
            </Alert>
          ) : ordersStatusQuery.data?.ordersSyncProgress && !ordersStatusQuery.data.ordersSyncProgress.running && ordersStatusQuery.data.ordersSyncProgress.completedAt && ordersSyncQueued !== null ? (
            <Alert tone="good" title="Synchronisation des commandes terminée avec succès">
              {ordersStatusQuery.data.ordersSyncProgress.forceFull
                ? `Import complet terminé : ${ordersStatusQuery.data.ordersSyncProgress.fetched.toLocaleString('fr-FR')} commandes synchronisées avec succès.`
                : `Synchronisation terminée : ${ordersStatusQuery.data.ordersSyncProgress.fetched} commandes mises à jour.`}
            </Alert>
          ) : syncOrders.isSuccess && ordersSyncQueued !== null ? (
            <div className="space-y-2 p-3 rounded-xl border border-herb-200 bg-herb-50/40">
              <ProgressBar value={null} label="Synchronisation envoyée au worker — initialisation en cours…" />
            </div>
          ) : null}

          {syncOrders.isError && (
            <Alert tone="alarm" title="Échec de la mise en file de la synchronisation">
              {String(syncOrders.error.message)}
            </Alert>
          )}
        </PanelBody>
      </Panel>

      {/* Site webhooks — near-real-time product refresh */}
      <Panel className="mt-4">
        <PanelBody className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SectionLabel>Webhooks du site (produits)</SectionLabel>
            {webhooksQuery.data !== undefined && webhooksQuery.data.ok && (
              <Badge tone={(webhooksQuery.data.webhooks?.length ?? 0) > 0 ? 'good' : 'signal'}>
                {webhooksQuery.data.webhooks?.length ?? 0} installé(s)
              </Badge>
            )}
          </div>
          <p className="text-xs leading-relaxed text-ink-soft">
            Installe sur le site les hooks <code className="font-mono">product.*</code> et{' '}
            <code className="font-mono">variation.*</code> : chaque création/modification/suppression
            prévient l&apos;application, qui met à jour la fiche en quelques secondes (le miroir
            nocturne reste le filet). Livraison signée HMAC vers{' '}
            <span className="font-mono">
              {(webhooksQuery.data?.deliveryUrl ?? cfg?.webhookDeliveryUrl ?? 'https://1405.tn') +
                '/api/v1/public/woo-webhook'}
            </span>
            .
          </p>
          {canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={installHooks.isPending}
                onClick={() => installHooks.mutate()}
                title="Crée ou réactive les 6 webhooks produit/variation sur le site, avec un secret HMAC conservé ici"
              >
                {installHooks.isPending
                  ? 'Installation…'
                  : cfg?.webhookConfigured === true
                    ? 'Réinstaller / réactiver'
                    : 'Installer les webhooks'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={webhooksQuery.isFetching}
                onClick={() => void webhooksQuery.refetch()}
              >
                Actualiser le statut
              </Button>
              {cfg?.webhookConfigured === true && (
                <span className="font-mono text-[11px] text-ink-faint">
                  secret {cfg.webhookSecretPreview ?? '••••'}
                </span>
              )}
            </div>
          )}
          {installHooks.isPending && (
            <div className="space-y-2 p-3 rounded-xl border border-clay-200 bg-clay-50/40">
              <ProgressBar value={null} label="Installation et vérification des webhooks sur WooCommerce…" />
            </div>
          )}
          {installHooks.isSuccess && (
            <Alert tone="good" title="Webhooks installés">
              {installHooks.data.created.length > 0 && (
                <div>créés : {installHooks.data.created.join(', ')}</div>
              )}
              {installHooks.data.updated.length > 0 && (
                <div>réactivés : {installHooks.data.updated.join(', ')}</div>
              )}
              <div className="mt-1">
                livraison vers <span className="font-mono">{installHooks.data.deliveryUrl}</span>
              </div>
            </Alert>
          )}
          {installHooks.isError && (
            <Alert tone="alarm" title="Échec de l’installation">
              {String(installHooks.error.message)}
            </Alert>
          )}
          {webhooksQuery.data !== undefined && !webhooksQuery.data.ok && (
            <Alert tone="warn" title="Statut indisponible">
              {webhooksQuery.data.error ?? 'Erreur inconnue'}
            </Alert>
          )}
          {webhooksQuery.data !== undefined && webhooksQuery.data.ok && (
            <div className="space-y-1">
              {(webhooksQuery.data.webhooks ?? []).map((w) => (
                <div
                  key={w.id}
                  className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-cream/40 px-3 py-1.5 font-mono text-xs"
                >
                  <Badge tone={w.status === 'active' ? 'good' : 'muted'}>{w.status}</Badge>
                  <span className="font-semibold">{w.topic}</span>
                  <span className="truncate text-ink-faint">{w.deliveryUrl}</span>
                </div>
              ))}
              {(webhooksQuery.data.webhooks ?? []).length === 0 && (
                <p className="text-xs text-ink-ghost">Aucun webhook installé sur le site.</p>
              )}
            </div>
          )}
        </PanelBody>
      </Panel>

      <Panel className="mt-4">
        <PanelBody className="space-y-3">
          <SectionLabel>Rapprochement des corrections</SectionLabel>
          <p className="text-xs leading-relaxed text-ink-soft">
            Vérification manuelle en <strong>lecture seule</strong> : lit chaque
            correction approuvée, compare aux valeurs réelles sur WooCommerce et
            prépare les lignes à corriger dans l&apos;onglet « À corriger (Woo) »
            du Workbench. Ce bouton <strong>n&apos;écrit jamais sur le site</strong>{' '}
            — aucune écriture n&apos;existe sans clic sur « Envoyer » /
            « Masquer », et aucun envoi n&apos;est planifié automatiquement.
          </p>
          {canEdit && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={testPush.isPending}
              onClick={() => testPush.mutate()}
            >
              {testPush.isPending ? 'Lancement…' : 'Lancer le rapprochement'}
            </Button>
          )}
          {testPush.isPending && (
            <div className="space-y-2 p-3 rounded-xl border border-clay-200 bg-clay-50/40">
              <ProgressBar value={null} label="Calcul et alignement du plan de rapprochement en cours…" />
            </div>
          )}
          {testPush.isError && (
            <Alert tone="alarm" title="Échec">{String(testPush.error.message)}</Alert>
          )}
        </PanelBody>
      </Panel>
      <Panel className="mt-4">
        <PanelBody className="space-y-3">
          <SectionLabel>Produits virtuels / téléchargeables (diagnostic)</SectionLabel>
          <p className="text-xs leading-relaxed text-ink-soft">
            Trouve les produits et variantes cochés « virtual » ou « downloadable »
            sur le site — sur un produit physique, ces cases suppriment l&apos;étape
            livraison au passage en caisse. Le scan lit le catalogue page par page
            (quelques minutes) ; vous corrigez ensuite chaque fiche dans Woo.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={flagScan.running}
                onClick={() => void runFlagScan()}
              >
                {flagScan.running ? `Scan page ${flagScan.page} / ${flagScan.totalPages || '…'}…` : 'Scanner virtual/downloadable'}
              </Button>
            )}
            {flagScan.found.length > 0 && (
              <Button type="button" variant="outline" size="sm" onClick={copyFlagList}>
                Copier la liste ({flagScan.found.length})
              </Button>
            )}
          </div>
          {flagScan.running && (
            <div className="space-y-2 p-3.5 rounded-xl border border-amber-200 bg-amber-50/50">
              <ProgressBar
                value={flagScan.totalPages > 0 ? Math.round((flagScan.page / flagScan.totalPages) * 100) : null}
                label={`Diagnostic en cours : page ${flagScan.page} / ${flagScan.totalPages || '…'} · ${flagScan.found.length} produit(s) anormal(aux)`}
              />
            </div>
          )}
          {flagScan.error !== null && (
            <Alert tone="alarm" title="Scan interrompu">
              {flagScan.error} — relancez, il reprend à la page 1 (les résultats déjà trouvés sont conservés ci-dessous).
            </Alert>
          )}
          {flagScan.found.length > 0 && (
            <div className="max-h-80 space-y-1.5 overflow-y-auto">
              {flagScan.found.map((f) => (
                <div
                  key={`${f.kind}-${f.id}`}
                  className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-cream/40 px-3 py-1.5 text-xs"
                >
                  <Badge tone={f.downloadable ? 'alarm' : 'signal'}>
                    {f.kind === 'variation' ? 'variante' : 'produit'}
                  </Badge>
                  <span className="font-mono">{f.sku !== '' ? f.sku : `#${f.id}`}</span>
                  <span className="min-w-0 flex-1 truncate text-ink-soft">{f.name}</span>
                  {f.virtual && <Badge tone="signal">virtual</Badge>}
                  {f.downloadable && <Badge tone="alarm">downloadable</Badge>}
                  <a
                    href={editUrl(f)}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-[11px] text-herb-700 underline"
                  >
                    corriger dans Woo ↗
                  </a>
                </div>
              ))}
            </div>
          )}
          {flagScan.done && flagScan.found.length === 0 && (
            <Alert tone="good" title="Scan terminé">
              Aucun produit ni variante virtuelle/téléchargeable — rien à corriger.
            </Alert>
          )}
        </PanelBody>
      </Panel>
    </div>
  );
}


export function WooEnginePage(): React.ReactElement {
  return <WooEnginePageInner />;
}
